import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { fetchAllDropdownListsStarted } from '../dropdown-lists/store';
import { fetchAllDocumentNamesStarted } from '../documents/store';
import { fetchAllClientDatasetsStarted } from '../client-datasets/store';
import { getPortfolioQuestion, getIsAskingQuestion, resetPortfolioSurvey, getIsDownloadingResults, getPortfolioAnalysisPageView, setPortfolioAnalysisPageView, PortfolioAnalysisPage, getSurveyIndex, setSurveyIndex, getPortfolioAnalyticsTemplatesStarted, getPortfolioResponse, getReportEmail } from './store';
import styles from './PortfolioAnalytics.module.scss';
import { PortfolioAnalyticsResults } from './results/PortfolioAnalyticsResults';
import { PortfolioAnalyticsDownloadModal } from './PortfolioAnalyticsDownloadModal';
import { PortfolioAnalyticsSurvey } from './survey/PortfolioAnalyticsSurvey';
import { PortfolioAnalysisSelect } from './PorfolioAnalyticsSelect';
import { PortfolioAnalyticsTemplates } from './PortfolioAnalyticsTemplates';
import { fetchAllEntitiesStarted } from '../entity/store';
import { PortfolioAnalyticsContinueModal } from './PortfolioAnalyticsContinueModal';
import { getLottieAnimation, LottieAntimationTypes } from '../../../assets/lottie/getLottieAnimation';
import { PortfolioAnalyticsEmailModal } from './PortfolioAnalyticsEmailModal';

export const PortfolioAnalytics: React.FC = () => {
    const [continueModalOpen, setContinueModalOpen] = useState(false);

    useFetchStarted([
        fetchAllClientDatasetsStarted(),
        fetchAllDropdownListsStarted(),
        fetchAllDocumentNamesStarted(),
        getPortfolioAnalyticsTemplatesStarted(),
        fetchAllEntitiesStarted()
    ]);

    const dispatch = useAppDispatch();

    const portfolioQuestion = useAppSelector(getPortfolioQuestion);
    const isAskingQuestion = useAppSelector(getIsAskingQuestion);
    const isDownloading = useAppSelector(getIsDownloadingResults);
    const pageView = useAppSelector(getPortfolioAnalysisPageView);
    const questionIndex = useAppSelector(getSurveyIndex);
    const portfolioResponse = useAppSelector(getPortfolioResponse);
    const reportEmail = useAppSelector(getReportEmail);

    const changePageView = useCallback((view: PortfolioAnalysisPage) => dispatch(setPortfolioAnalysisPageView(view)), [dispatch]);

    const setQuestionIndex = useCallback((index: number) => dispatch(setSurveyIndex(index)), [dispatch]);

    const restartSurvey = useCallback(() => {
        dispatch(resetPortfolioSurvey());
        changePageView(PortfolioAnalysisPage.SURVEY);
    }, [dispatch, changePageView]);

    const backToTemplates = useCallback(() => {
        dispatch(resetPortfolioSurvey());
        changePageView(PortfolioAnalysisPage.TEMPLATES);
    }, [dispatch, changePageView]);

    const hasMounted = useRef(false);

    useEffect(() => {
        // Only run on initial mount
        if (!hasMounted.current) {
            hasMounted.current = true;

            // If we already have a portfolioResponse when the component mounts we are navigating back to it
            if (!isNull(portfolioResponse)) {
                setContinueModalOpen(true);
            }
        }
    }, [portfolioResponse]);

    // We use useRef with the pageView as using the pageView in the unmount useEffect would trigger this every time
    // the pageView changes, however useRef persists across renders and does not trigger re-renders
    const latestPageViewRef = useRef(pageView);

    useEffect(() => {
        latestPageViewRef.current = pageView;
    }, [pageView]);

    useEffect(() => () => {
        const lastPageView = latestPageViewRef.current;
        if (lastPageView === PortfolioAnalysisPage.SURVEY || lastPageView === PortfolioAnalysisPage.TEMPLATES) {
            dispatch(resetPortfolioSurvey());
            changePageView(PortfolioAnalysisPage.SELECT);
        }
    }, [changePageView, dispatch]);

    const animation = useMemo(() => getLottieAnimation(LottieAntimationTypes.ANIMATION), []);

    const pageContent = useMemo(() => {
        switch (pageView) {
            case PortfolioAnalysisPage.SURVEY:
                return <PortfolioAnalyticsSurvey
                    questionIndex={questionIndex}
                    setQuestionIndex={setQuestionIndex}
                    portfolioQuestion={portfolioQuestion}
                    backToTemplates={backToTemplates}
                />;
            case PortfolioAnalysisPage.TEMPLATES:
                return <PortfolioAnalyticsTemplates changePageView={changePageView} />;
            case PortfolioAnalysisPage.RESULTS:
                return <PortfolioAnalyticsResults
                    restartSurvey={restartSurvey}
                    portfolioQuestion={portfolioQuestion}
                    changePageView={changePageView}
                    backToTemplates={backToTemplates}
                />;
            case PortfolioAnalysisPage.SELECT:
            default:
                return <PortfolioAnalysisSelect selectTile={changePageView} />;
        }
    }, [questionIndex, portfolioQuestion, restartSurvey, changePageView, pageView, setQuestionIndex, backToTemplates]);

    if (isAskingQuestion || isDownloading) {
        return (
            <div className={styles.loadingAnimationWrapper}>
                <div className={styles.lottieAnimationWrapper}>
                    <Lottie animationData={animation} loop />
                </div>
                <div className={styles.lottieAnimationMessage}>Analysing your portfolio of selected documents.</div>
            </div>
        );
    }

    return (
        <>
            {pageContent}
            <PortfolioAnalyticsDownloadModal />
            {!isNull(reportEmail) && <PortfolioAnalyticsEmailModal reportEmail={reportEmail} />}
            <PortfolioAnalyticsContinueModal isOpen={continueModalOpen} closeModal={setContinueModalOpen} />
        </>
    );
};
